export type JsonDecoder<T> = (value: unknown) => T | undefined
export type TextDecoder<T> = (value: string) => T | undefined

/**
 * Read and decode one JSON value from localStorage.
 *
 * Storage is an optional capability: SSR, disabled/private storage, malformed JSON,
 * and values rejected by the caller's decoder all resolve to the supplied fallback.
 */
export function readJson<T>(key: string, fallback: T, decode: JsonDecoder<T>): T {
  const raw = readText(key, undefined, (value) => value)
  if (raw === undefined) return fallback

  try {
    const decoded = decode(JSON.parse(raw) as unknown)
    return decoded === undefined ? fallback : decoded
  } catch {
    return fallback
  }
}

/** Write one JSON value when storage is available, tolerating serialization/write failures. */
export function writeJson(key: string, value: unknown): void {
  try {
    const encoded = JSON.stringify(value)
    if (encoded !== undefined) writeText(key, encoded)
  } catch {
    /* cyclic values and other serialization failures are non-fatal */
  }
}

/** Read a legacy/plain-text value with the same optional-storage policy as readJson. */
export function readText<T>(key: string, fallback: T, decode: TextDecoder<T>): T {
  try {
    if (typeof localStorage === 'undefined') return fallback
    const raw = localStorage.getItem(key)
    if (raw === null) return fallback
    const decoded = decode(raw)
    return decoded === undefined ? fallback : decoded
  } catch {
    return fallback
  }
}

/** Write a legacy/plain-text value when storage is available. */
export function writeText(key: string, value: string): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(key, value)
  } catch {
    /* storage full, disabled, or private mode: keep the in-memory value */
  }
}
